import axios from 'axios'
import type { AuthData } from '../../types/auth/authData'

const googleButtonContainerId = 'google-signin-hidden-container'

let initPromise: Promise<void> | null = null
let googleApiKey: string | null = null
let pendingResolve: ((value: AuthData) => void) | null = null
let pendingReject: ((reason?: any) => void) | null = null

function ensureGoogleInitialized(clientId: string): Promise<void> {
  if (initPromise) return initPromise

  initPromise = new Promise<void>((resolve, reject) => {
    try {
      if (!window.google?.accounts?.id) {
        reject(new Error('Google Identity Services client not loaded'))
        initPromise = null
        return
      }

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response: any) => {
          if (!pendingResolve || !pendingReject) return

          if (!response || !response.credential) {
            pendingReject(new Error('No credential returned from Google'))
            pendingResolve = null
            pendingReject = null
            return
          }

          try {
            if (!googleApiKey) throw new Error('Missing Google API key')
            const googleIdToken = response.credential
            const url = `https://identitytoolkit.googleapis.com/v1/accounts:signInWithIdp?key=${googleApiKey}`
            const payload = {
              postBody: `id_token=${googleIdToken}&providerId=google.com`,
              requestUri: window.location.origin,
              returnSecureToken: true,
            }

            const { data } = await axios.post(url, payload, {
              headers: { 'Content-Type': 'application/json' },
            })

            const authData: AuthData = {
              kind: data.kind,
              localId: data.localId,
              email: data.email,
              displayName: data.displayName,
              idToken: data.idToken,
              registered: true,
              profilePicture: data.photoUrl,
              refreshToken: data.refreshToken,
              expiresIn: data.expiresIn,
            }

            pendingResolve(authData)
          } catch (err) {
            pendingReject(err)
          } finally {
            pendingResolve = null
            pendingReject = null
          }
        },
        ux_mode: 'popup',
      })

      let container = document.getElementById(googleButtonContainerId) as HTMLElement | null
      if (!container) {
        container = document.createElement('div')
        container.id = googleButtonContainerId
        container.style.position = 'fixed'
        container.style.top = '-9999px'
        container.style.left = '-9999px'
        container.style.pointerEvents = 'none'
        document.body.appendChild(container)
      }

      window.google.accounts.id.renderButton(container, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
      })

      resolve()
    } catch (err) {
      initPromise = null
      reject(err)
    }
  })

  return initPromise
}

export async function signInWithGoogleAsync(): Promise<AuthData> {
  const clientId = import.meta.env.VITE_AUTH_GOOGLE_CLIENT_ID
  const apiKey = import.meta.env.VITE_AUTH_GIP_API_KEY

  if (!clientId || !apiKey) {
    throw new Error('Missing Google Sign-In configuration')
  }

  googleApiKey = apiKey

  await ensureGoogleInitialized(clientId)

  return new Promise<AuthData>(async (resolve, reject) => {
    pendingResolve = resolve
    pendingReject = reject

    let container = document.getElementById(googleButtonContainerId)
    let button = container?.querySelector('div[role="button"], button') as HTMLElement | null

    if (!button) {
      initPromise = null
      try {
        await ensureGoogleInitialized(clientId)
        container = document.getElementById(googleButtonContainerId)
        button = container?.querySelector('div[role="button"], button') as HTMLElement | null
      } catch (e) {
        pendingResolve = null
        pendingReject = null
        reject(e)
        return
      }
    }

    if (!button) {
      pendingResolve = null
      pendingReject = null
      reject(new Error('Google Sign-In button not found'))
      return
    }

    button.click()

    const timeout = setTimeout(() => {
      if (pendingReject) {
        pendingReject(new Error('Google Sign-In was cancelled or timed out'))
        pendingResolve = null
        pendingReject = null
      }
      clearTimeout(timeout)
    }, 120000)
  })
}
